import {
    type Finra3210Payload,
    formatInvestmentProfileValue,
    NetWorth,
    ResidencyStatus,
    type UserInvestmentProfile,
    YesNo,
} from '@fruitful/investment-management';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    NavigationRow,
    RowList,
    ScreenContainer,
    ScrollView,
    Spinner,
    View,
    YStack,
} from 'smoothie';

import KycButton from '../../banking/components/kyc/steps/KycButton';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import { useGetUserInvestmentProfile } from '../hooks/useGetUserInvestmentProfile';
import useUpsertUserInvestmentProfile from '../hooks/useUpsertUserInvestmentProfile';
import { CountrySelectionStep } from './CountrySelectionStep';
import { InvestmentProfileQuestionStep } from './InvestmentProfileQuestionStep';
import {
    type Finra3210Document,
    StockExchangeMemberStep,
} from './StockExchangeMemberStep';

export interface BackgroundIndexChangeInfo {
    index: number;
    isSequenceComplete: boolean;
    isOnOverview: boolean;
}

interface BackgroundSequenceProps {
    /** userId must be provided to avoid needing to call the useAuthedUser hook in this component, which sometimes renders in a portal */
    userId: string;
    onBack: () => void;
    onIndexChange?: (info: BackgroundIndexChangeInfo) => void;
    onCompleteSubmit: (finra3210?: Finra3210Payload) => void;
    providedIndex?: number;
    isInsideModal?: boolean;
    collectFinra3210?: boolean;
}

enum BackgroundStep {
    CITIZENSHIP = 'CITIZENSHIP',
    RESIDENCY_STATUS = 'RESIDENCY_STATUS',
    NET_WORTH = 'NET_WORTH',
    SHAREHOLDER = 'SHAREHOLDER',
    STOCK_EXCHANGE = 'STOCK_EXCHANGE',
    BOARD_MEMBER = 'BOARD_MEMBER',
    POLITICAL_EXPOSURE = 'POLITICAL_EXPOSURE',
    BACKUP_WITHHOLDING = 'BACKUP_WITHHOLDING',
    OVERVIEW = 'OVERVIEW',
}

/* Base question steps that always appear */
const BASE_QUESTION_STEPS = [
    BackgroundStep.CITIZENSHIP,
    BackgroundStep.NET_WORTH,
    BackgroundStep.SHAREHOLDER,
    BackgroundStep.STOCK_EXCHANGE,
    BackgroundStep.BOARD_MEMBER,
    BackgroundStep.POLITICAL_EXPOSURE,
    BackgroundStep.BACKUP_WITHHOLDING,
] as const;

/* Non-citizen steps inserted after CITIZENSHIP when user is not a US citizen */
const NON_CITIZEN_STEPS = [BackgroundStep.RESIDENCY_STATUS] as const;

/* UserInvestmentProfile fields collected in this sequence */
type BackgroundField = keyof Pick<
    UserInvestmentProfile,
    | 'citizenship'
    | 'residency'
    | 'netWorth'
    | 'shareholderOfPublicCompany'
    | 'stockExchangeMember'
    | 'boardOfPublicCompany'
    | 'politicalExposure'
    | 'backupWithholding'
>;

interface StepConfig {
    title: string;
    question: string;
    field: BackgroundField;
    keys?: Record<string, string>;
}

/** Residency choices in this flow; N/A is omitted as a selectable option. */
const RESIDENCY_STATUS_QUESTION_OPTIONS = {
    PERMANENT_RESIDENT: ResidencyStatus.PERMANENT_RESIDENT,
    TEMPORARY_RESIDENT: ResidencyStatus.TEMPORARY_RESIDENT,
    NONRESIDENT: ResidencyStatus.NONRESIDENT,
} as const;

const STEP_CONFIGS: Partial<Record<BackgroundStep, StepConfig>> = {
    [BackgroundStep.CITIZENSHIP]: {
        title: 'Citizenship',
        question: 'What is your citizenship country?',
        field: 'citizenship',
    },
    [BackgroundStep.RESIDENCY_STATUS]: {
        title: 'Residency status',
        question: 'What is your residency status in the US?',
        field: 'residency',
        keys: RESIDENCY_STATUS_QUESTION_OPTIONS,
    },
    [BackgroundStep.NET_WORTH]: {
        title: 'Net worth',
        question: 'What is your current net worth?',
        field: 'netWorth',
        keys: NetWorth,
    },
    [BackgroundStep.SHAREHOLDER]: {
        title: 'Shareholder of public company',
        question:
            'Are you or a family member a senior executive or 10% shareholder at a publicly traded company?',
        field: 'shareholderOfPublicCompany',
        keys: YesNo,
    },
    [BackgroundStep.STOCK_EXCHANGE]: {
        title: 'Stock exchange member',
        question:
            'Are you or a family member employed by a Brokerage Firm, Securities Exchange, or FINRA?',
        field: 'stockExchangeMember',
        keys: YesNo,
    },
    [BackgroundStep.BOARD_MEMBER]: {
        title: 'Board of public company',
        question: 'Is your family on the board of a public company?',
        field: 'boardOfPublicCompany',
        keys: YesNo,
    },
    [BackgroundStep.POLITICAL_EXPOSURE]: {
        title: 'Political exposure',
        question:
            'Are you or a member of your family a current or former high-level elected or appointed public official?',
        field: 'politicalExposure',
        keys: YesNo,
    },
    [BackgroundStep.BACKUP_WITHHOLDING]: {
        title: 'IRS backup withholding',
        question:
            'Are you subject to IRS backup withholding? Backup withholding applies if the IRS has notified you that you must have taxes withheld due to certain reporting issues.',
        field: 'backupWithholding',
        keys: YesNo,
    },
};

/* Helper to assert step is non-nullish */
const requireStepConfig = (step: BackgroundStep): StepConfig => {
    const config = STEP_CONFIGS[step];
    if (config == null) {
        throw new Error(`Missing STEP_CONFIGS for step: ${step}`);
    }
    return config;
};

const isNonCitizen = (profile: Partial<UserInvestmentProfile>) =>
    profile.citizenship != null && profile.citizenship !== 'US';

/* Check whether a given step has a real answer in the profile. */
const isStepAnswered = (
    stepKey: BackgroundStep,
    data: Partial<UserInvestmentProfile>,
): boolean => {
    const config = STEP_CONFIGS[stepKey];
    if (config == null) return false;
    return data[config.field] != null;
};

/* Build the dynamic step sequence based on citizenship status */
const buildSequence = (profile: Partial<UserInvestmentProfile>) => {
    const questionSteps: BackgroundStep[] = isNonCitizen(profile)
        ? [
              BackgroundStep.CITIZENSHIP,
              ...NON_CITIZEN_STEPS,
              ...BASE_QUESTION_STEPS.slice(1),
          ]
        : [...BASE_QUESTION_STEPS];

    return {
        questionSteps,
        fullSequence: [...questionSteps, BackgroundStep.OVERVIEW],
    };
};

export const OVERVIEW_TITLE = 'Background information';

const getStepTitle = (step: BackgroundStep): string => {
    if (step === BackgroundStep.OVERVIEW) return OVERVIEW_TITLE;
    return STEP_CONFIGS[step]?.title ?? '';
};

export const BackgroundSequence = ({
    userId,
    onBack,
    onIndexChange,
    onCompleteSubmit,
    providedIndex,
    isInsideModal = false,
    collectFinra3210 = false,
}: BackgroundSequenceProps) => {
    useTrackViewOnFocus('Profile/BackgroundSequence');
    const [index, setIndexState] = useState(providedIndex ?? 0);
    const [isReady, setIsReady] = useState(false);
    const [profile, setProfile] = useState<Partial<UserInvestmentProfile>>({});
    const [finraFirmName, setFinraFirmName] = useState('');
    const [finraDocument, setFinraDocument] =
        useState<Finra3210Document | null>(null);

    const { data: existingProfile, isLoading: isLoadingProfile } =
        useGetUserInvestmentProfile({ userId });
    const { mutateAsync: upsertProfile, isPending: isUpdatingProfile } =
        useUpsertUserInvestmentProfile();

    const { questionSteps, fullSequence } = useMemo(
        () => buildSequence(profile),
        [profile],
    );

    const routes = useMemo(
        () => fullSequence.map((key) => ({ key })),
        [fullSequence],
    );

    const checkCompleteness = useCallback(
        (data: Partial<UserInvestmentProfile>) => {
            const { questionSteps: steps } = buildSequence(data);
            const areProfileQuestionsComplete = steps.every((stepKey) =>
                isStepAnswered(stepKey, data),
            );
            const isFinraDocumentComplete =
                !collectFinra3210 ||
                data.stockExchangeMember !== YesNo.YES ||
                (finraFirmName.trim() !== '' && finraDocument != null);
            return areProfileQuestionsComplete && isFinraDocumentComplete;
        },
        [collectFinra3210, finraDocument, finraFirmName],
    );

    const isComplete = useMemo(
        () => checkCompleteness(profile),
        [checkCompleteness, profile],
    );

    const setIndex = useCallback(
        (newIndex: number) => {
            setIndexState(newIndex);
            onIndexChange?.({
                index: newIndex,
                isSequenceComplete: checkCompleteness(profile),
                isOnOverview:
                    fullSequence[newIndex] === BackgroundStep.OVERVIEW,
            });
        },
        [onIndexChange, checkCompleteness, profile, fullSequence],
    );

    /**
     * Called when the user selects an option on a question step.
     * For the citizenship step this handles special branching:
     *   US  → write citizenship='US' + residency=NOT_APPLICABLE, skip residency
     *   Other → write the country code, clear residency so user must answer it next
     */
    const onOptionSelect = async (
        updates: Partial<UserInvestmentProfile>,
        shouldAdvance = true,
    ) => {
        const updatedProfile = { ...profile, ...updates };
        const isOnCitizenshipStep =
            fullSequence[index] === BackgroundStep.CITIZENSHIP &&
            updates.citizenship != null;

        /* Build the upsert body, applying citizenship-specific side effects */
        let body: Partial<UserInvestmentProfile> = updates;
        if (isOnCitizenshipStep && updates.citizenship === 'US') {
            updatedProfile.residency = ResidencyStatus.NOT_APPLICABLE;
            body = {
                citizenship: 'US',
                residency: ResidencyStatus.NOT_APPLICABLE,
            };
        } else if (isOnCitizenshipStep) {
            updatedProfile.residency = undefined;
            body = { citizenship: updates.citizenship };
        }

        await upsertProfile({ body, urlParams: { userId } });
        setProfile(updatedProfile);

        /* Recompute sequence with the updated profile to get correct indices */
        const { questionSteps: newSteps, fullSequence: newSequence } =
            buildSequence(updatedProfile);
        const newOverviewIndex = newSequence.indexOf(BackgroundStep.OVERVIEW);
        const newLastQuestionIndex = newSteps.length - 1;

        const allComplete = newSteps.every((stepKey) =>
            isStepAnswered(stepKey, updatedProfile),
        );

        if (!shouldAdvance) {
            return;
        }

        if (allComplete || index === newLastQuestionIndex) {
            setIndex(newOverviewIndex);
        } else {
            /* Find the current step in the new sequence and advance */
            const currentStep = fullSequence[index] as
                | BackgroundStep
                | undefined;
            const newCurrentIndex =
                currentStep != null ? newSequence.indexOf(currentStep) : -1;
            setIndex(newCurrentIndex >= 0 ? newCurrentIndex + 1 : index + 1);
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: BackgroundStep } }) => {
        const stepKey = route.key;

        if (stepKey === BackgroundStep.OVERVIEW) {
            return (
                <ScrollView contentContainerStyle={{ height: '100%' }}>
                    <YStack
                        paddingTop={16}
                        gap={16}
                        height="100%"
                        justifyContent={
                            isInsideModal ? 'space-between' : 'flex-start'
                        }
                    >
                        <RowList>
                            {questionSteps.map((key, stepIndex) => {
                                const config = requireStepConfig(key);
                                const fieldValue = profile[config.field];
                                const displayValue =
                                    formatInvestmentProfileValue(
                                        config.field,
                                        (fieldValue as string) ?? null,
                                    );
                                return (
                                    <NavigationRow
                                        key={key}
                                        title={config.title}
                                        subtitle={displayValue}
                                        titleProps={{
                                            numberOfLines: 2,
                                            ellipsizeMode: 'tail',
                                        }}
                                        onPress={() => {
                                            setIndex(stepIndex);
                                        }}
                                    />
                                );
                            })}
                        </RowList>
                        <KycButton
                            text={isInsideModal ? 'Continue' : 'Done'}
                            onPress={() => {
                                if (
                                    profile.stockExchangeMember === YesNo.YES &&
                                    finraDocument != null
                                ) {
                                    onCompleteSubmit({
                                        firmName: finraFirmName.trim(),
                                        fileContentBase64:
                                            finraDocument.fileContentBase64,
                                        mimeType: finraDocument.mimeType,
                                    });
                                } else {
                                    onCompleteSubmit();
                                }
                            }}
                            disabled={!isComplete}
                            hideSecureText={!isInsideModal}
                        />
                    </YStack>
                </ScrollView>
            );
        }

        if (stepKey === BackgroundStep.CITIZENSHIP) {
            return (
                <CountrySelectionStep
                    question={requireStepConfig(stepKey).question}
                    initialValue={profile.citizenship}
                    onSelect={onOptionSelect}
                    isPending={isUpdatingProfile}
                />
            );
        }

        const config = requireStepConfig(stepKey);

        if (stepKey === BackgroundStep.STOCK_EXCHANGE && collectFinra3210) {
            return (
                <StockExchangeMemberStep
                    question={config.question}
                    initialValue={profile.stockExchangeMember}
                    firmName={finraFirmName}
                    document={finraDocument}
                    isPending={isUpdatingProfile}
                    onFirmNameChange={setFinraFirmName}
                    onDocumentChange={setFinraDocument}
                    onSelect={async (updates) => {
                        const selectedValue = updates.stockExchangeMember;
                        await onOptionSelect(
                            updates,
                            selectedValue === YesNo.NO,
                        );
                    }}
                    onContinue={() => {
                        const areProfileQuestionsComplete = questionSteps.every(
                            (step) => isStepAnswered(step, profile),
                        );
                        setIndex(
                            areProfileQuestionsComplete
                                ? fullSequence.indexOf(BackgroundStep.OVERVIEW)
                                : index + 1,
                        );
                    }}
                />
            );
        }

        return (
            <InvestmentProfileQuestionStep
                key={profile[config.field]}
                question={config.question}
                options={config.keys as Record<string, string>}
                initialValue={profile[config.field]}
                field={config.field}
                onSelect={onOptionSelect}
                isPending={isUpdatingProfile}
            />
        );
    };

    const currentStep: BackgroundStep =
        fullSequence[index] ?? fullSequence[0] ?? BackgroundStep.CITIZENSHIP;
    const isOnOverview = currentStep === BackgroundStep.OVERVIEW;

    useEffect(() => {
        if (!isLoadingProfile) {
            if (existingProfile) {
                const { fullSequence: seq } = buildSequence(existingProfile);
                const areProfileQuestionsComplete = seq
                    .filter((step) => step !== BackgroundStep.OVERVIEW)
                    .every((step) => isStepAnswered(step, existingProfile));
                if (areProfileQuestionsComplete) {
                    const needsFinraDocument =
                        collectFinra3210 &&
                        existingProfile.stockExchangeMember === YesNo.YES;
                    setIndex(
                        seq.indexOf(
                            needsFinraDocument
                                ? BackgroundStep.STOCK_EXCHANGE
                                : BackgroundStep.OVERVIEW,
                        ),
                    );
                }
                setProfile(existingProfile);
            }
            setIsReady(true);
        }
    }, [collectFinra3210, isLoadingProfile, existingProfile]);

    useEffect(() => {
        if (providedIndex != null && !isNaN(providedIndex)) {
            setIndexState(providedIndex);
        }
    }, [providedIndex]);

    if (!isReady)
        return (
            <YStack flex={1} alignItems="center" justifyContent="center">
                <Spinner />
            </YStack>
        );

    if (isInsideModal) {
        return (
            <View height="100%">
                <TabView
                    lazy
                    animationEnabled={!isComplete}
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    renderTabBar={() => null}
                    onIndexChange={setIndex}
                />
            </View>
        );
    }

    return (
        <ScreenContainer
            title={getStepTitle(currentStep)}
            hasBackButton
            onBack={() => {
                if (index === 0 || isOnOverview) {
                    onBack?.();
                } else {
                    setIndex(index - 1);
                }
            }}
        >
            <View height="100%">
                <TabView
                    animationEnabled={!isComplete}
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    renderTabBar={() => null}
                    onIndexChange={setIndex}
                />
            </View>
        </ScreenContainer>
    );
};
